// Wire types and URL helpers for the conversations-api artifacts endpoints.
// Mirrors `ArtifactPreviewResponse`, `ArtifactDownloadRequest/Result`, and
// `ArtifactExportRequest/Result` in `product/ai/libs/conversations-types`.

const { VITE_CONVERSATIONS_API } = import.meta.env;

// "image" isn't a stored ArtifactType in the backend taxonomy — the
// conversations preview service synthesizes it as a render hint for image
// blobs (see product/ai/services/conversations/.../preview_service.py).
export type ArtifactType = "dataframe" | "figure" | "value" | "text" | "blob" | "image";

// Output formats accepted by the convert-and-copy Lambda. Not every type
// supports every format; see `formatOptionsFor` in `artifacts/formats.ts`.
export type ConvertAndCopyFormat = "csv" | "parquet" | "zip" | "html" | "pickle" | "original";

export type ConvertAndCopyStatus = "ready" | "processing";

export type ArtifactContent =
  | Record<string, unknown>
  | unknown[]
  | string
  | number
  | boolean
  | null;

export interface ArtifactPreview {
  artifact_id: string;
  artifact_type: ArtifactType;
  artifact_name: string | null;
  filename: string | null;
  summary: string | null;
  code: string | null;
  content: ArtifactContent;
  generated_at: string;
  version: number;
}

export interface ArtifactDownloadRequest {
  format: ConvertAndCopyFormat;
}

export interface ArtifactDownloadResult {
  download_url: string;
  file_name: string;
  content_type: string;
  status: ConvertAndCopyStatus;
}

export interface ArtifactExportRequest {
  project_id: number;
  format: ConvertAndCopyFormat;
}

export interface ArtifactExportResult {
  file_name: string;
  file_path: string;
  file_size_bytes: number;
  s3_key: string;
}

export const artifactPreviewsPath = (conversationId: string, names: string[]) => {
  const params = new URLSearchParams();
  for (const name of names) params.append("names", name);
  return `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/previews?${params.toString()}`;
};

export const artifactDownloadPath = (conversationId: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/${artifactId}/download`;

export const artifactExportPath = (conversationId: string, artifactId: string) =>
  `${VITE_CONVERSATIONS_API}/${conversationId}/artifacts/${artifactId}/export`;

// Lineage is currently served by analysis-agent (there's a TODO to move it
// to conversations-api). The response is a directed-graph payload with
// nodes/edges plus a pre-rendered Mermaid string.
export interface ArtifactLineageNode {
  artifact_id: string;
  // The legacy types carry more fields, but the renderer only needs to
  // know "is there anything to draw" — leave the rest opaque.
}

export interface ArtifactLineageEdge {
  from: string;
  to: string;
}

export interface ArtifactLineageGraph {
  nodes: ArtifactLineageNode[];
  edges: ArtifactLineageEdge[];
  mermaid: string;
}

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

export const artifactLineagePath = (conversationId: string, artifactId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/artifacts/${artifactId}/lineage`;
